"use client";
import React from "react";
import Image from "next/image";
import { Box, Container, Typography, Paper } from "@mui/material";
import { IMAGES } from "../constants/images";
import { useTranslation } from "react-i18next";

const ExpectedTimeline = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const stepsRaw = t("expectedTimeline.steps", { returnObjects: true });
  const stepsData = Array.isArray(stepsRaw) ? stepsRaw : [];

  const stepImages = [
    IMAGES.ET_ONE_IMG,
    IMAGES.ET_TWO_IMG,
    IMAGES.ET_THREE_IMG,
  ];

  const timelineSteps = stepsData.map((step, i) => ({
    id: i + 1,
    image: stepImages[i],
    alt: step.alt,
    title: step.title,
    description: step.description,
  }));

  return (
    <Container
      maxWidth="lg"
      sx={{ py: { xs: 3, md: 4 }, direction: isArabic ? "rtl" : "ltr" }}
    >
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: "bold",
            mb: 1,
            color: "#0A0A0A",
            fontFamily: "var(--font-plusjakarta)",
            fontSize: {
              xs: "1.5rem",
              sm: "1.75rem",
              md: "1.8rem",
              lg: "1.8rem",
              xl: "2.1rem",
            },
            mt: { xs: "5%", md: "3%" },
            px: { xs: 2, sm: 0 },
          }}
        >
          {t("expectedTimeline.title")}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "#4A5565",
            fontSize: {
              xs: "1rem",
              sm: "1rem",
              md: "1rem",
              lg: "1rem",
              xl: "1.2rem",
            },
            fontFamily: "var(--font-outfit)",
            fontWeight: 400,
            px: { xs: 2, sm: 3, md: 0 },
          }}
        >
          {t("expectedTimeline.subtitle")}
        </Typography>
      </Box>

      {/* Steps */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: { xs: 4, sm: 3, md: 3 },
          position: "relative",
          flexDirection: { xs: "column", lg: "row" },
          px: { xs: 2, sm: 1, md: 0 },
        }}
      >
        {timelineSteps.map((step) => (
          <Box
            key={step.id}
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              zIndex: 1,
              width: "100%",
            }}
          >
            {/* Image */}
            <Box
              sx={{
                width: { xs: 100, sm: 110, md: 120 },
                height: { xs: 100, sm: 110, md: 120 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <Image
                src={step.image}
                alt={step.alt}
                width={300}
                height={400}
                style={{ objectFit: "contain" }}
              />
            </Box>

            {/* Content Card */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 2.5, md: 3 },
                textAlign: "center",
                borderRadius: 2,
                minHeight: { xs: "auto", md: 180 },
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                width: "100%",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  mb: 2,
                  color: "#0A0A0A",
                  fontSize: {
                    xs: "1.1rem",
                    sm: "1.1rem",
                    md: "1.1rem",
                    lg: "1.1rem",
                    xl: "1.3rem",
                  },
                  fontFamily: "var(--font-plusjakarta)",
                  minHeight: { xs: "auto", md: "60px" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {step.title}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#4A5565",
                  fontSize: { xs: "0.9rem", sm: "0.92rem", md: "0.95rem" },
                  fontFamily: "var(--font-outfit)",
                  textAlign: "center",
                  minHeight: { xs: "auto", md: "80px" },
                  display: "flex",
                  alignItems: "flex-start",
                  lineHeight: 1.6,
                }}
              >
                {step.description}
              </Typography>

              {/* Dots */}
              <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                <Image
                  src={IMAGES.THREE_DOTS_IMG}
                  alt="dots"
                  width={30}
                  height={10}
                  style={{ objectFit: "contain" }}
                />
              </Box>
            </Paper>
          </Box>
        ))}
      </Box>
    </Container>
  );
};

export default ExpectedTimeline;
